import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Alert,
  IconButton,
  Chip,
  Tooltip,
  Button,
  TextField,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  CircularProgress
} from '@mui/material';
import {
  DataGrid,
  GridActionsCellItem,
  GridRowEditStopReasons,
} from '@mui/x-data-grid';
import {
  Delete as DeleteIcon,
  Save as SaveIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Add as AddIcon,
  CalendarMonth as CalendarIcon,
  Today as TodayIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { startOfWeek, addWeeks, format, addDays, isSameWeek, parseISO, getDay } from 'date-fns';
import { es } from 'date-fns/locale';
import { personasAPI, dedicacionesAPI, iniciativasAPI, semanasConfirmadasAPI } from '../services/api';

const TimeTrackingCalendar = () => {
  const [searchParams] = useSearchParams();
  // Helper function to identify if initiative should be displayed as checkbox
  const isVacationOrLeave = (project) => {
    if (!project) return false;
    const isGeneric = project.tipo_de_iniciativa === 'generica';
    const name = project.nombre_de_la_oportunidad?.toLowerCase() || '';
    const isVacationRelated = name.includes('baja') || name.includes('vacaciones') || name.includes('ausencia');
    return isGeneric && isVacationRelated;
  };

  // Helper function to identify public holidays (non-working days)
  const isHoliday = (date) => {
    if (!date) return false;
    
    const year = date.getFullYear();
    const month = date.getMonth() + 1; // JavaScript months are 0-indexed
    const day = date.getDate();
    
    // Known holidays for Madrid 2025
    const holidays = [
      // 2025
      { year: 2025, month: 1, day: 1 },   // Año Nuevo
      { year: 2025, month: 1, day: 6 },   // Epifanía del Señor (Reyes)
      { year: 2025, month: 4, day: 17 },  // Jueves Santo
      { year: 2025, month: 4, day: 18 },  // Viernes Santo
      { year: 2025, month: 5, day: 1 },   // Fiesta del Trabajo
      { year: 2025, month: 5, day: 2 },   // Fiesta de la Comunidad de Madrid
      { year: 2025, month: 5, day: 15 },  // San Isidro (Madrid)
      { year: 2025, month: 8, day: 15 },  // Asunción de la Virgen
      { year: 2025, month: 10, day: 12 }, // Fiesta Nacional de España
      { year: 2025, month: 11, day: 1 },  // Todos los Santos
      { year: 2025, month: 11, day: 10 },  // Almudena
      { year: 2025, month: 12, day: 6 },  // Día de la Constitución
      { year: 2025, month: 12, day: 8 },  // Inmaculada Concepción
      { year: 2025, month: 12, day: 24 }, // Navidad
      { year: 2025, month: 12, day: 25 }, // Navidad
      { year: 2025, month: 12, day: 31 }, // Navidad
      // 2026
      { year: 2026, month: 1, day: 1 },   // Año Nuevo
      { year: 2026, month: 1, day: 6 },   // Epifanía del Señor (Reyes)
      { year: 2026, month: 4, day: 2 },   // Jueves Santo
      { year: 2026, month: 4, day: 3 },   // Viernes Santo
      { year: 2026, month: 5, day: 1 },   // Fiesta del Trabajo
      { year: 2026, month: 5, day: 2 },   // Fiesta de la Comunidad de Madrid
      { year: 2026, month: 5, day: 15 },  // San Isidro (Madrid)
      { year: 2026, month: 10, day: 12 }, // Fiesta Nacional de España
      { year: 2026, month: 11, day: 2 },  // Todos los santos (Madrid)
      { year: 2026, month: 11, day: 9 },  // Virgen de la Almudena (Madrid)
      { year: 2026, month: 12, day: 7 },  // Día de la Constitución (trasladado)
      { year: 2026, month: 12, day: 8 },  // Inmaculada Concepción
      { year: 2026, month: 12, day: 24 }, // Navidad
      { year: 2026, month: 12, day: 25 }, // Navidad
      { year: 2026, month: 12, day: 31 }, // Navidad
    ];
    
    return holidays.some(holiday => 
      holiday.year === year && 
      holiday.month === month && 
      holiday.day === day
    );
  };

  // Helper function to identify becarios (interns) by email (.bec@acciona.com)
  const checkIsBecario = (email) => {
    return typeof email === 'string' && email.toLowerCase().includes('.bec@acciona.com');
  };

  // Helper function to identify jornada intensiva (summer schedule) days
  const isSummer = (date) => {
    if (!date) return false;
    // Jornada intensiva: 22 June 2026 to 31 August 2026 (inclusive)
    const start = new Date(2026, 5, 22); // Months are 0-indexed: 5 = June
    const end = new Date(2026, 7, 31);   // 7 = August
    // Remove time part for comparison
    const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    return d >= start && d <= end;
  };

  // Helper function to calculate expected hours for a week (considering holidays, summer schedule and becarios)
  const getExpectedWeeklyHours = (weekStart) => {
    const days = [];
    for (let i = 0; i < 5; i++) { // Monday to Friday
      const date = addDays(weekStart, i);
      const dayOfWeek = getDay(date);
      const isHolidayDay = isHoliday(date);
      const isSummerDay = isSummer(date);
      // Becarios: 5h/day. Others: If holiday 0h, summer 7h, Friday 6h, Mon-Thu 8h
      let maxHours = 0;
      if (!isHolidayDay) {
        if (isBecario) {
          maxHours = 5;
        } else if (isSummerDay) {
          maxHours = 7;
        } else {
          maxHours = (dayOfWeek === 5 ? 6 : 8);
        }
      }
      days.push(maxHours);
    }
    return days.reduce((total, hours) => total + hours, 0);
  };

  // Helper function to check if all working days are complete
  const areAllWorkingDaysComplete = () => {
    if (projectData.length === 0) return false;
    
    // Get total hours for each day across all projects
    const dayTotals = {};
    weekDays.forEach(day => {
      dayTotals[day.dateKey] = 0;
    });
    
    projectData.filter(p => !p.isTotal).forEach(project => {
      weekDays.forEach(day => {
        const dayData = project.days[day.dateKey];
        if (dayData) {
          dayTotals[day.dateKey] += dayData.hours || 0;
        }
      });
    });
    
    // Check if each working day meets its expected hours
    for (const day of weekDays) {
      const expectedHours = day.maxHours || 0;
      if (expectedHours > 0 && dayTotals[day.dateKey] < expectedHours) {
        return false; // Found a working day that's not complete
      }
    }
    
    return true; // All working days are complete
  };

  // Helper function to get week key for tracking confirmed weeks
  const getWeekKey = (weekStart, personId) => {
    return `${personId}-${format(weekStart, 'yyyy-MM-dd')}`;
  };

  // Helper function to check if current week is confirmed
  const isCurrentWeekConfirmed = () => {
    const weekKey = getWeekKey(currentWeekStart, selectedPerson);
    return confirmedWeeks.has(weekKey);
  };

  // Helper function to distribute weekly hours proportionally across working days
  const distributeWeeklyHours = (totalWeeklyHours) => {
    const workingDays = weekDays.filter(day => day.maxHours > 0); // Only days with max hours (not holidays)
    
    if (workingDays.length === 0) return {};
    
    // Total capacity of the week (e.g. Mon-Thu 8h + Fri 6h = 38h)
    const totalCapacity = workingDays.reduce((sum, day) => sum + day.maxHours, 0);
    
    // Cap at total capacity
    const hoursToDistribute = Math.min(totalWeeklyHours, totalCapacity);
    
    // Distribute proportionally to each day's maxHours, rounded to 0.5h increments.
    // The last working day receives whatever hours remain (capped at its maxHours)
    // so the total always equals hoursToDistribute exactly.
    const distribution = {};
    let allocated = 0;
    
    workingDays.forEach((day, index) => {
      let dayShare;
      if (index === workingDays.length - 1) {
        // Give the remainder to the last day, capped at its maxHours
        dayShare = Math.min(
          Math.round((hoursToDistribute - allocated) * 2) / 2,
          day.maxHours
        );
      } else {
        // Proportional share rounded to nearest 0.5h, capped at maxHours
        dayShare = Math.min(
          Math.round((day.maxHours / totalCapacity) * hoursToDistribute * 2) / 2,
          day.maxHours
        );
        allocated += dayShare;
      }
      distribution[day.dateKey] = dayShare;
    });
    
    return distribution;
  };

  // State management
  const [selectedPerson, setSelectedPerson] = useState('');
  const [selectedPersonName, setSelectedPersonName] = useState('');
  const [isBecario, setIsBecario] = useState(false);
  const [people, setPeople] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [currentWeekStart, setCurrentWeekStart] = useState(() => {
    const weekParam = searchParams.get('week');
    let initialWeek;
    
    if (weekParam) {
      try {
        const paramDate = parseISO(weekParam);
        initialWeek = startOfWeek(paramDate, { weekStartsOn: 1 });
        console.log('Using week from URL parameter:', format(initialWeek, 'yyyy-MM-dd (EEEE)', { locale: es }));
      } catch (error) {
        console.error('Invalid week parameter, using current week:', error);
        initialWeek = startOfWeek(new Date(), { weekStartsOn: 1 });
      }
    } else {
      const today = new Date();
      initialWeek = startOfWeek(today, { weekStartsOn: 1 });
      console.log('Today is:', format(today, 'yyyy-MM-dd (EEEE)', { locale: es }));
    }
    
    console.log('Current week start initialized to:', format(initialWeek, 'yyyy-MM-dd (EEEE)', { locale: es }));
    return initialWeek;
  });
  const [projectData, setProjectData] = useState([]); // Transposed view: each row is a project
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState([]);
  const [genericProjectsDialog, setGenericProjectsDialog] = useState(false);
  const [availableGenericProjects, setAvailableGenericProjects] = useState([]);
  const [loadingGenericProjects, setLoadingGenericProjects] = useState(false);
  const [selectedGenericProjects, setSelectedGenericProjects] = useState(new Set());
  
  // Calculate if current week directly (not as state to avoid stale values)
  const today = new Date();
  const todayWeekStart = startOfWeek(today, { weekStartsOn: 1 });
  const isCurrentWeek = format(currentWeekStart, 'yyyy-MM-dd') === format(todayWeekStart, 'yyyy-MM-dd');
  
  // Week confirmation states
  const [showConfirmWeekDialog, setShowConfirmWeekDialog] = useState(false);
  const [weeklyTotalHours, setWeeklyTotalHours] = useState(0);
  const [isWeekComplete, setIsWeekComplete] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [canConfirmWeek, setCanConfirmWeek] = useState(false);
  const [confirmedWeeks, setConfirmedWeeks] = useState(new Set()); // Store confirmed week keys
  
  // States for notification modals
  const [notificationModal, setNotificationModal] = useState({
    open: false,
    title: '',
    message: '',
    type: 'success', // 'success', 'error', 'info'
    onClose: null
  });
  
  // Helper function to show notification modals
  const showNotification = (title, message, type = 'success', onClose = null) => {
    setNotificationModal({
      open: true,
      title,
      message,
      type,
      onClose
    });
  };
  
  // Helper function to close notification modal
  const closeNotification = () => {
    const { onClose } = notificationModal;
    setNotificationModal({
      open: false,
      title: '',
      message: '',
      type: 'success',
      onClose: null
    });
    if (onClose) {
      onClose();
    }
  };
  
  // Reference for hidden date input
  const hiddenDateInputRef = useRef(null);
  
  // Generate days for the current week (Monday to Friday)
  const weekDays = useMemo(() => {
    const days = [];
    for (let i = 0; i < 5; i++) { // Monday to Friday
      const date = addDays(currentWeekStart, i);
      const dayOfWeek = getDay(date);
      const isHolidayDay = isHoliday(date);
      const isSummerDay = isSummer(date);
      // Becarios: 5h/day. Others: If holiday 0h, summer 7h, Friday 6h, Mon-Thu 8h
      let maxHours = 0;
      if (!isHolidayDay) {
        if (isBecario) {
          maxHours = 5;
        } else if (isSummerDay) {
          maxHours = 7;
        } else {
          maxHours = (dayOfWeek === 5 ? 6 : 8);
        }
      }
      
      days.push({
        date: date,
        dateKey: format(date, 'yyyy_MM_dd'),
        label: format(date, 'EEEE dd/MM', { locale: es }),
        shortLabel: format(date, 'EEE dd', { locale: es }),
        maxHours: maxHours,
        isToday: format(date, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd'),
        isHoliday: isHolidayDay,
        isSummer: isSummerDay
      });
    }
    console.log('Generated week days:', days.map(d => ({ 
      label: d.label, 
      maxHours: d.maxHours, 
      isToday: d.isToday, 
      isHoliday: d.isHoliday,
      isSummer: d.isSummer
    })));
    return days;
  }, [currentWeekStart, isBecario]);

  // Load initial data
  useEffect(() => {
    loadCurrentUserData();
  }, []);

  // Handle URL week parameter changes
  useEffect(() => {
    const weekParam = searchParams.get('week');
    if (weekParam) {
      try {
        const paramDate = parseISO(weekParam);
        const weekFromParam = startOfWeek(paramDate, { weekStartsOn: 1 });
        const currentWeek = format(currentWeekStart, 'yyyy-MM-dd');
        const newWeek = format(weekFromParam, 'yyyy-MM-dd');
        
        if (currentWeek !== newWeek) {
          console.log('URL week parameter changed, updating current week:', newWeek);
          setCurrentWeekStart(weekFromParam);
        }
      } catch (error) {
        console.error('Invalid week parameter:', error);
      }
    }
  }, [searchParams]);

  // Load person's daily data when person OR week changes
  useEffect(() => {
    console.log('📅 useEffect triggered - currentWeekStart changed to:', format(currentWeekStart, 'yyyy-MM-dd'));
    if (selectedPerson) {
      console.log('Loading data due to person/week change:', {
        person: selectedPerson,
        week: format(currentWeekStart, 'yyyy-MM-dd')
      });
      loadPersonDailyData();
      loadConfirmedWeeks(); // Cargar semanas confirmadas desde la API
    } else {
      // Clear data when no person selected
      setProjectData([]);
      setProjects([]);
      setConfirmedWeeks(new Set()); // Clear confirmed weeks
    }
  }, [selectedPerson, currentWeekStart]);

  // Load generic projects when dialog opens
  useEffect(() => {
    if (genericProjectsDialog) {
      loadGenericProjects();
      setSelectedGenericProjects(new Set()); // Clear previous selection
    }
  }, [genericProjectsDialog]);

  // Calculate weekly totals and check if week is complete
  useEffect(() => {
    if (projectData.length > 0) {
      // Calculate total hours across all projects and days (exclude TOTAL_DIA row)
      const totalHours = projectData.filter(p => !p.isTotal).reduce((sum, project) => {
        return sum + (project.totalWeeklyHours || 0);
      }, 0);
      
      const expectedHours = getExpectedWeeklyHours(currentWeekStart);
      const allDaysComplete = areAllWorkingDaysComplete();
      
      setWeeklyTotalHours(totalHours);
      setIsWeekComplete(totalHours >= expectedHours && allDaysComplete);
      setHasUnsavedChanges(true); // Mark as having unsaved changes when data changes
      
      console.log('📊 Weekly hours calculation:', {
        totalHours,
        expectedHours,
        allDaysComplete,
        isComplete: totalHours >= expectedHours && allDaysComplete
      });
    }
  }, [projectData, currentWeekStart, isBecario]);

  const loadGenericProjects = async () => {
    setLoadingGenericProjects(true);
    try {
      const response = await iniciativasAPI.getGenericasDisponibles();
      const genericProjects = response.data || [];
      
      // Filter out projects that are already in the current week
      const currentProjectIds = new Set(projects.map(p => p.id));
      const availableProjects = genericProjects.filter(project => !currentProjectIds.has(project.id));
      
      setAvailableGenericProjects(availableProjects);
    } catch (error) {
      console.error('Error loading generic projects:', error);
      setErrors(['Error al cargar las iniciativas genéricas disponibles']);
    } finally {
      setLoadingGenericProjects(false);
    }
  };

  const loadCurrentUserData = async () => {
    try {
      // Obtener información del usuario autenticado
      const currentUserResponse = await personasAPI.getCurrentUser();
      const currentUserPerson = currentUserResponse.data.persona;
      const userIsAdmin = currentUserResponse.data.is_admin;
      
      console.log('🔐 Usuario logueado:', {
        username: currentUserResponse.data.username,
        isAdmin: userIsAdmin,
        persona: currentUserPerson
      });
      
      setIsAdmin(userIsAdmin);
      
      if (userIsAdmin) {
        // ADMIN: Cargar todas las personas y permitir selección
        console.log('👑 Usuario admin - cargando todas las personas');
        const response = await personasAPI.getAll();
        const peopleData = response.data.results || response.data;
        
        // Sort people alphabetically by full_name
        const sortedPeople = peopleData.sort((a, b) => {
          const nameA = (a.full_name || a.nombre || '').toLowerCase();
          const nameB = (b.full_name || b.nombre || '').toLowerCase();
          return nameA.localeCompare(nameB);
        });
        
        setPeople(sortedPeople);
        
        // Auto-seleccionar la persona del admin si tiene una asociada
        if (currentUserPerson) {
          console.log('🎯 Admin con persona asociada, auto-seleccionando:', currentUserPerson.full_name);
          setSelectedPerson(currentUserPerson.id.toString());
          setSelectedPersonName(currentUserPerson.full_name);
          setIsBecario(checkIsBecario(currentUserPerson.email));
        } else {
          console.log('👑 Admin sin persona asociada - debe seleccionar manualmente');
          // No auto-seleccionar nada, el admin debe elegir
        }
      } else {
        // USUARIO NORMAL: Solo cargar su persona asociada
        if (currentUserPerson) {
          console.log('🎯 Usuario normal - cargando solo su persona:', currentUserPerson.full_name);
          setSelectedPerson(currentUserPerson.id.toString());
          setSelectedPersonName(currentUserPerson.full_name);
          setIsBecario(checkIsBecario(currentUserPerson.email));
          setPeople([currentUserPerson]);
        } else {
          setErrors(['Usuario no tiene una persona asociada. Contacte al administrador.']);
        }
      }
      
    } catch (error) {
      console.error('❌ Error loading user data:', error);
      if (error.response?.status === 404) {
        setErrors(['Usuario no tiene una persona asociada. Contacte al administrador.']);
      } else {
        setErrors(['Error al cargar los datos del usuario']);
      }
    }
  };

  // Load confirmed weeks from API
  const loadConfirmedWeeks = async () => {
    if (!selectedPerson) return;
    
    try {
      console.log('🔄 Cargando semanas confirmadas para persona:', selectedPerson);
      const weekKeys = await semanasConfirmadasAPI.getConfirmedWeekKeys(selectedPerson);
      setConfirmedWeeks(new Set(weekKeys));
      console.log('📅 Semanas confirmadas cargadas desde API:', weekKeys);
      
      // Debug: verificar si la semana actual debería estar confirmada
      const currentWeekKey = getWeekKey(currentWeekStart, selectedPerson);
      console.log('🔍 Week key actual:', currentWeekKey);
      console.log('🔍 ¿Está confirmada la semana actual?', weekKeys.includes(currentWeekKey));
      
    } catch (error) {
      console.error('❌ Error cargando semanas confirmadas:', error);
      // No mostrar error al usuario, mantener funcionamiento básico
      setConfirmedWeeks(new Set());
    }
  };

  const loadPersonDailyData = async () => {
    if (!selectedPerson) return;
    
    setLoading(true);
    setErrors([]);
    
    try {
      // Calculate date range for the current week
      const startDate = currentWeekStart;
      const endDate = addDays(currentWeekStart, 4); // Friday
      
      console.log('🔄 Loading data for person:', selectedPerson);
      console.log('📅 Week start date:', format(startDate, 'yyyy-MM-dd'));
      console.log('🔑 Current week key:', getWeekKey(startDate, selectedPerson));
      console.log('📋 Confirmed weeks:', Array.from(confirmedWeeks));
      console.log('✅ Is current week confirmed?', confirmedWeeks.has(getWeekKey(startDate, selectedPerson)));
      
      // STEP 1: Get person's last dedication date to determine editability
      console.log('🔍 Getting person\'s last dedication date...');
      const allDedicationsResponse = await dedicacionesAPI.getByPersona(selectedPerson, {});
      const allPersonDedications = allDedicationsResponse.data?.results || allDedicationsResponse.data || [];
      
      // Find last dedication date for this person
      let lastDedicationDate = null;
      const today = new Date();
      const todayString = format(today, 'yyyy-MM-dd');
      
      if (allPersonDedications.length > 0) {
        console.log('🔍 All dedications for this person:', allPersonDedications);
        
        // Filter out future dedications (only consider up to today)
        const validDedications = allPersonDedications.filter(d => {
          const dedicationDateStr = format(parseISO(d.fecha), 'yyyy-MM-dd');
          return dedicationDateStr <= todayString;
        });
        
        console.log('📅 Valid dedications (up to today):', validDedications.length);
        console.log('📅 Future dedications filtered out:', allPersonDedications.length - validDedications.length);
        
        if (validDedications.length > 0) {
          // Get all valid dedications, sort by date descending, and take the first one
          const sortedValidDedications = validDedications
            .sort((a, b) => new Date(b.fecha) - new Date(a.fecha)); // Descending order
          
          const mostRecent = sortedValidDedications[0];
          lastDedicationDate = parseISO(mostRecent.fecha);
          
          console.log('📅 Last real dedication found:', {
            fecha: mostRecent.fecha,
            iniciativa: mostRecent.iniciativa,
            horas: mostRecent.horas_imputadas || mostRecent.horas,
            person: mostRecent.persona
          });
          console.log('📅 Last real dedication date found:', format(lastDedicationDate, 'yyyy-MM-dd'));
        } else {
          console.log('📅 No valid dedications found (all were future dates)');
        }
        
        console.log('📅 Total dedications:', allPersonDedications.length);
      } else {
        console.log('📅 No previous dedications found - all weeks editable');
      }
      
      // Determine if current week is editable
      // NEW LOGIC: Weeks are editable unless explicitly confirmed as completed
      const weekKey = getWeekKey(currentWeekStart, selectedPerson);
      const isWeekConfirmed = confirmedWeeks.has(weekKey);
      const isEditableWeek = !isWeekConfirmed; // Not editable if confirmed
        
      console.log('📅 Week editability:', {
        weekStart: format(startDate, 'yyyy-MM-dd'),
        weekEnd: format(endDate, 'yyyy-MM-dd'),
        lastDedication: lastDedicationDate ? format(lastDedicationDate, 'yyyy-MM-dd') : 'none',
        today: format(today, 'yyyy-MM-dd'),
        weekKey: weekKey,
        isWeekConfirmed: isWeekConfirmed,
        isEditable: isEditableWeek,
        note: 'Week editable unless confirmed complete'
      });
      
      // STEP 2: Get dedications first to check if this is a historical week
      const dedicationsParams = {
        fecha__gte: format(startDate, 'yyyy-MM-dd'),
        fecha__lte: format(endDate, 'yyyy-MM-dd')
      };
      console.log('🔍 Requesting dedications with params:', dedicationsParams);
      console.log('🔍 Week range:', format(startDate, 'yyyy-MM-dd'), 'to', format(endDate, 'yyyy-MM-dd'));
      
      const dedicationsResponse = await dedicacionesAPI.getByPersona(selectedPerson, dedicationsParams);
      
      // Get projects for this specific week
      let projectsResponse;
      try {
        projectsResponse = await personasAPI.getIniciativas(selectedPerson, {
          fecha_inicio: format(startDate, 'yyyy-MM-dd'),
          fecha_fin: format(endDate, 'yyyy-MM-dd')
        });
      } catch (error) {
        console.error('❌ Error loading projects:', error);
        projectsResponse = { data: [] };
      }
      
      console.log('🔍 Dedications API response:', {
        responseType: typeof dedicationsResponse.data,
        hasResults: dedicationsResponse.data?.results ? 'yes' : 'no',
        resultsLength: dedicationsResponse.data?.results?.length || 'N/A',
        directDataLength: Array.isArray(dedicationsResponse.data) ? dedicationsResponse.data.length : 'not array',
        rawResponse: dedicationsResponse.data
      });
      
      const personProjects = projectsResponse.data || [];
      const allDedications = dedicationsResponse.data?.results || dedicationsResponse.data || [];
      
      console.log('🔍 Filtering dedications for week 2025-11-24 to 2025-11-28');
      console.log('📊 All dedications received:', allDedications.length);
      console.log('📅 Week start:', startDate, 'Week end:', endDate);
      
      // Filter dedications for this specific person and week
      const dedications = allDedications.filter((dedication, index) => {
        if (!dedication.fecha) {
          if (index < 5) console.log(`❌ Dedication ${index}: No fecha field`);
          return false;
        }
        
        const dedicationDate = parseISO(dedication.fecha);
        const isInWeek = dedicationDate >= startDate && dedicationDate <= endDate;
        const isFromSelectedPerson = dedication.persona === parseInt(selectedPerson);
        
        // Log a few examples, especially the ones from nov 24
        if (dedication.fecha === '2025-11-24' || index < 5) {
          console.log(`🔍 Dedication ${index} (${dedication.fecha}):`, {
            fecha: dedication.fecha,
            persona: dedication.persona,
            selectedPerson: selectedPerson,
            iniciativa: dedication.iniciativa,
            horas: dedication.horas || dedication.horas_imputadas,
            dedicationDate: dedicationDate,
            isInWeek: isInWeek,
            isFromSelectedPerson: isFromSelectedPerson,
            passes: isInWeek && isFromSelectedPerson
          });
        }
        
        return isInWeek && isFromSelectedPerson;
      });
      
      console.log('✅ Final filtered dedications:', dedications.length);
      
      console.log('📊 Week data loaded:', {
        weekType: isEditableWeek ? 'EDITABLE' : 'HISTÓRICA',
        projects: personProjects.length,
        dedications: dedications.length,
        projectDetails: personProjects.map(p => ({ id: p.id, nombre: p.nombre_de_la_oportunidad })),
        dedicationDetails: dedications.map(d => ({ fecha: d.fecha, horas: d.horas || d.horas_imputadas, iniciativa: d.iniciativa, iniciativa_nombre: d.iniciativa_nombre })),
        weekRange: `${format(startDate, 'yyyy-MM-dd')} to ${format(endDate, 'yyyy-MM-dd')}`
      });
      
      // STEP 3: Build projects list
      let finalProjects = personProjects;
      
      // Always check for historical dedications to ensure we show all projects with data
      const uniqueIniciativaIds = [...new Set(dedications.map(d => d.iniciativa).filter(Boolean))];
      
      if (uniqueIniciativaIds.length > 0) {
        console.log('🔧 Ensuring historical projects are included...');
        
        // Create a set of current project IDs for quick lookup
        const currentProjectIds = new Set(personProjects.map(p => p.id));
        
        // Add any missing projects from dedications
        const missingProjects = uniqueIniciativaIds
          .filter(id => !currentProjectIds.has(id))
          .map(iniciativaId => {
            const dedication = dedications.find(d => d.iniciativa === iniciativaId);
            return {
              id: iniciativaId,
              nombre_de_la_oportunidad: dedication?.iniciativa_nombre || `Iniciativa ${iniciativaId}`,
              tipo_de_iniciativa: dedication?.iniciativa_tipo || 'historical',
              estado: 'historical' // Mark as historical so we know it came from dedications
            };
          });
          
        if (missingProjects.length > 0) {
          console.log('📋 Adding missing historical projects:', missingProjects.map(p => ({ id: p.id, nombre: p.nombre_de_la_oportunidad })));
          finalProjects = [...personProjects, ...missingProjects];
        }
      }
      
      console.log('🏗️ Final projects:', finalProjects.map(p => ({ id: p.id, nombre: p.nombre_de_la_oportunidad })));
      setProjects(finalProjects);

      // STEP 4: Build project data structure (TRANSPOSED)
      const projectDataMap = new Map();
      
      // Initialize all projects with their days
      finalProjects.forEach(project => {
        const projectId = project.id;
        const isCheckbox = isVacationOrLeave(project);
        
        projectDataMap.set(projectId, {
          id: `project_${projectId}`,
          projectId: projectId,
          projectName: project.nombre_de_la_oportunidad || project.nombre || `Proyecto ${projectId}`,
          project: project,
          days: {},
          totalWeeklyHours: 0
        });
        
        // Initialize days for this project
        weekDays.forEach(day => {
          const dayKey = day.dateKey;
          
          // Determine cell editability based on week editability and project state
          let cellIsEditable = false;
          
          // First check: if week is confirmed, NOTHING is editable
          const weekKey = getWeekKey(currentWeekStart, selectedPerson);
          const isWeekConfirmed = confirmedWeeks.has(weekKey);
          
          if (!isWeekConfirmed && isEditableWeek && !day.isHoliday) {
            // Only if week is NOT confirmed, check other conditions
            // For generic projects, they are always editable in editable weeks
            // For regular projects, only "en_curso" projects can be modified
            // For historical projects, they are also editable to allow adding more hours
            const isGenericProject = project.tipo_de_iniciativa === 'generica';
            const isHistoricalProject = project.estado === 'historical';
            if (isGenericProject || project.estado === 'en_curso' || isHistoricalProject) {
              cellIsEditable = true;
            }
          }
          
          console.log(`📅 Cell editability check for project ${project.id} on ${format(day.date, 'yyyy-MM-dd')}:`, {
            isEditableWeek,
            isHoliday: day.isHoliday,
            projectState: project.estado,
            projectType: project.tipo_de_iniciativa,
            isGenericProject: project.tipo_de_iniciativa === 'generica',
            isHistoricalProject: project.estado === 'historical',
            weekKey: getWeekKey(currentWeekStart, selectedPerson),
            isWeekConfirmed: confirmedWeeks.has(getWeekKey(currentWeekStart, selectedPerson)),
            dayDate: format(day.date, 'yyyy-MM-dd'),
            cellIsEditable,
            note: 'Cells editable only if week not confirmed and other conditions met'
          });
          
          projectDataMap.get(projectId).days[dayKey] = {
            hours: 0,
            isCheckbox: isCheckbox,
            isChecked: false,
            isEditable: cellIsEditable,
            dayInfo: day
          };
        });
      });
      
      // STEP 5: Fill in actual dedication data
      dedications.forEach(dedication => {
        const dedicationDate = parseISO(dedication.fecha);
        const dateKey = format(dedicationDate, 'yyyy_MM_dd');
        const projectId = dedication.iniciativa;
        const hours = parseFloat(dedication.horas || dedication.horas_imputadas || 0);
        
        console.log(`🔍 Processing dedication:`, {
          fecha: dedication.fecha,
          dateKey: dateKey,
          iniciativa: projectId,
          horas_raw: dedication.horas,
          horas_imputadas_raw: dedication.horas_imputadas,
          horas_parsed: hours,
          hasProject: projectDataMap.has(projectId),
          hasDayKey: projectDataMap.has(projectId) ? Object.hasOwnProperty.call(projectDataMap.get(projectId).days, dateKey) : false
        });
        
        if (projectDataMap.has(projectId) && projectId) {
          const projectData = projectDataMap.get(projectId);
          
          if (projectData.days.hasOwnProperty(dateKey)) {
            const dayData = projectData.days[dateKey];
            
            if (dayData.isCheckbox) {
              dayData.isChecked = hours > 0;
              dayData.hours = hours;
            } else {
              dayData.hours = hours;
            }
            
            // Keep the editability that was calculated before (don't override it)
            console.log(`📊 Set project ${projectId} on ${dateKey}: ${hours} hours (editable: ${dayData.isEditable})`);
          } else {
            console.log(`⚠️ Day ${dateKey} not found in project ${projectId} days:`, Object.keys(projectData.days));
          }
        } else {
          console.log(`⚠️ Project ${projectId} not found in project data map or projectId is missing`);
        }
      });
      
      // STEP 6: Calculate weekly totals for each project
      projectDataMap.forEach((projectData, projectId) => {
        projectData.totalWeeklyHours = Object.values(projectData.days).reduce(
          (sum, dayData) => sum + dayData.hours, 0
        );
      });

      const finalProjectData = Array.from(projectDataMap.values())
        .sort((a, b) => a.projectName.localeCompare(b.projectName));
      
      console.log('🏁 Final project data summary:', {
        totalProjects: finalProjectData.length,
        totalDays: weekDays.length,
        editableCells: finalProjectData.reduce((count, project) => 
          count + Object.values(project.days).filter(d => d.isEditable).length, 0),
        cellsWithHours: finalProjectData.reduce((count, project) => 
          count + Object.values(project.days).filter(d => d.hours > 0).length, 0),
        weekType: isEditableWeek ? 'EDITABLE' : 'HISTORICAL'
      });
      
      console.log('📊 Project weekly totals:', finalProjectData.map(p => ({ 
        project: p.projectName, 
        totalHours: p.totalWeeklyHours 
      })));
      
      // Add TOTAL ROW at the end to show daily totals
      const totalRow = {
        id: 'TOTAL_DIA',
        projectId: 'TOTAL_DIA',
        projectName: 'TOTAL DÍA',
        project: { tipo_de_iniciativa: 'total_row', estado: 'total' },
        days: {},
        totalWeeklyHours: 0,
        isTotal: true
      };
      
      // Calculate totals for each day
      weekDays.forEach(day => {
        const dayKey = day.dateKey;
        let dayTotal = 0;
        finalProjectData.forEach(project => {
          if (project.days[dayKey]) {
            dayTotal += project.days[dayKey].hours || 0;
          }
        });
        totalRow.days[dayKey] = {
          hours: dayTotal,
          isCheckbox: false,
          isChecked: false,
          isEditable: false,
          dayInfo: day
        };
        totalRow.totalWeeklyHours += dayTotal;
      });
      
      finalProjectData.push(totalRow);
      
      setProjectData(finalProjectData);
      
      // Reset unsaved changes state when loading fresh data
      setHasUnsavedChanges(false);
      setCanConfirmWeek(false);
      
    } catch (error) {
      console.error('Error loading daily data:', error);
      const errorMessage = error.response?.data?.detail 
        || error.response?.data?.message 
        || error.message 
        || 'Error al cargar los datos diarios';
      setErrors([errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  // Calculate total hours for each day
  const getDayTotal = (dayData) => {
    return Object.values(dayData.projects).reduce((sum, projectData) => sum + projectData.hours, 0);
  };
  
  // Recalculate weekly totals for all projects
  const recalculateTotals = (dataArray) => {
    return dataArray.map(project => {
      const totalWeeklyHours = Object.values(project.days).reduce(
        (sum, dayData) => sum + dayData.hours, 0
      );
      return {
        ...project,
        totalWeeklyHours
      };
    });
  };

  // Helper function to recalculate TOTAL_DIA row
  const recalculateDailyTotals = (dataArray) => {
    const totalRowIndex = dataArray.findIndex(p => p.isTotal);
    
    if (totalRowIndex === -1) return dataArray; // No total row found
    
    const regularProjects = dataArray.filter(p => !p.isTotal);
    const totalRow = { ...dataArray[totalRowIndex] };
    totalRow.days = { ...totalRow.days };
    totalRow.totalWeeklyHours = 0;
    
    // Recalculate totals for each day
    weekDays.forEach(day => {
      const dayKey = day.dateKey;
      let dayTotal = 0;
      
      regularProjects.forEach(project => {
        if (project.days && project.days[dayKey]) {
          dayTotal += project.days[dayKey].hours || 0;
        }
      });
      
      totalRow.days[dayKey] = {
        ...totalRow.days[dayKey],
        hours: dayTotal
      };
      totalRow.totalWeeklyHours += dayTotal;
    });
    
    const newData = [...dataArray];
    newData[totalRowIndex] = totalRow;
    return newData;
  };

  // Update project hours for an entire week (distribute proportionally)
  const updateProjectWeeklyHours = (projectId, weeklyHours) => {
    setHasUnsavedChanges(true);
    setCanConfirmWeek(false); // Reset confirmation status when data changes
    
    const distribution = distributeWeeklyHours(weeklyHours);
    
    setProjectData(prev => {
      const updatedData = prev.map(project => {
        if (project.projectId === projectId) {
          const newDays = { ...project.days };
          
          // Update hours for each day based on distribution
          Object.keys(distribution).forEach(dayKey => {
            if (newDays[dayKey]) {
              newDays[dayKey] = {
                ...newDays[dayKey],
                hours: distribution[dayKey]
              };
            }
          });
          
          // Calculate new total
          const totalWeeklyHours = Object.values(newDays).reduce(
            (sum, dayData) => sum + dayData.hours, 0
          );
          
          return {
            ...project,
            days: newDays,
            totalWeeklyHours
          };
        }
        return project;
      });
      
      // Recalculate TOTAL_DIA row
      return recalculateDailyTotals(updatedData);
    });
  };
  
  const updateProjectHours = (projectId, dayKey, hours) => {
    setHasUnsavedChanges(true);
    setCanConfirmWeek(false); // Reset confirmation status when data changes
    
    setProjectData(prev => {
      const updatedData = prev.map(project => {
        if (project.projectId === projectId) {
          const newDays = { ...project.days };
          
          if (newDays[dayKey]) {
            newDays[dayKey] = {
              ...newDays[dayKey],
              hours: hours
            };
          }
          
          // Recalculate total
          const totalWeeklyHours = Object.values(newDays).reduce(
            (sum, dayData) => sum + dayData.hours, 0
          );
          
          return {
            ...project,
            days: newDays,
            totalWeeklyHours
          };
        }
        return project;
      });
      
      // Recalculate TOTAL_DIA row
      return recalculateDailyTotals(updatedData);
    });
  };

  // Update checkbox state for vacation/leave projects
  const updateProjectCheckbox = (projectId, dayKey, isChecked) => {
    setHasUnsavedChanges(true);
    setCanConfirmWeek(false); // Reset confirmation status when data changes
    
    setProjectData(prev => {
      const updatedData = prev.map(project => {
        if (project.projectId === projectId) {
          const newDays = { ...project.days };
          
          if (newDays[dayKey] && newDays[dayKey].isCheckbox) {
            // Use the day's maxHours (becarios: 5h; others: 8h Mon-Thu, 6h Fri, 0h holidays)
            const dayMaxHours = newDays[dayKey].dayInfo?.maxHours || (isBecario ? 5 : 8);
            newDays[dayKey] = {
              ...newDays[dayKey],
              isChecked: isChecked,
              hours: isChecked ? dayMaxHours : 0
            };
          }
          
          // Recalculate total
          const totalWeeklyHours = Object.values(newDays).reduce(
            (sum, dayData) => sum + dayData.hours, 0
          );
          
          return {
            ...project,
            days: newDays,
            totalWeeklyHours
          };
        }
        return project;
      });
      
      // Recalculate TOTAL_DIA row
      return recalculateDailyTotals(updatedData);
    });
  };

  // Handle row updates from DataGrid
  const processRowUpdate = (newRow) => {
    setProjectData(prev => prev.map(row => row.id === newRow.id ? newRow : row));
    return newRow;
  };

  const handleProcessRowUpdateError = (error) => {
    console.error('Row update error:', error);
    setErrors(['Error al actualizar la fila']);
  };

  // Add generic project to current week
  const addGenericProject = (genericProject) => {
    setHasUnsavedChanges(true);
    setCanConfirmWeek(false); // Reset confirmation status when data changes
    
    // Add to projects list
    setProjects(prev => [...prev, genericProject]);
    
    // Add to project data structure
    const isCheckbox = isVacationOrLeave(genericProject);
    const newProjectData = {
      id: `project_${genericProject.id}`,
      projectId: genericProject.id,
      projectName: genericProject.nombre_de_la_oportunidad || genericProject.nombre || `Proyecto ${genericProject.id}`,
      project: genericProject,
      days: {},
      totalWeeklyHours: 0
    };
    
    // Initialize days for this project
    weekDays.forEach(day => {
      const dayKey = day.dateKey;
      newProjectData.days[dayKey] = {
        hours: 0,
        isCheckbox: isCheckbox,
        isChecked: false,
        isEditable: true, // Generic projects are always editable when added to current week
        dayInfo: day
      };
    });
    
    setProjectData(prev => {
      // Insert before TOTAL_DIA row
      const totalRowIndex = prev.findIndex(p => p.isTotal);
      if (totalRowIndex !== -1) {
        const newData = [...prev];
        newData.splice(totalRowIndex, 0, newProjectData);
        return newData;
      }
      return [...prev, newProjectData];
    });
  };

  // Add multiple generic projects at once
  const addMultipleGenericProjects = () => {
    if (selectedGenericProjects.size === 0) return;
    
    // Add all selected projects
    selectedGenericProjects.forEach(projectId => {
      const project = availableGenericProjects.find(p => p.id === projectId);
      if (project) {
        addGenericProject(project);
      }
    });
    
    // Clear selection and close dialog
    setSelectedGenericProjects(new Set());
    setGenericProjectsDialog(false);
  };

  // Toggle project selection
  const toggleProjectSelection = (projectId) => {
    setSelectedGenericProjects(prev => {
      const newSet = new Set(prev);
      if (newSet.has(projectId)) {
        newSet.delete(projectId);
      } else {
        newSet.add(projectId);
      }
      return newSet;
    });
  };

  // Remove project from current week (only for manually added generic projects)
  const removeProject = (projectId) => {
    // Remove from projects list
    setProjects(prev => prev.filter(p => p.id !== projectId));
    
    // Remove from project data structure and recalculate totals
    setProjectData(prev => {
      const filteredData = prev.filter(project => project.projectId !== projectId);
      return recalculateDailyTotals(filteredData);
    });
  };

  // Week confirmation functions
  const handleConfirmWeekComplete = () => {
    if (isWeekComplete) {
      setShowConfirmWeekDialog(true);
    }
  };

  const confirmWeekCompletion = async () => {
    // Preservar la semana actual antes de hacer cambios
    const weekToConfirm = currentWeekStart;
    
    try {
      console.log('🔒 Confirming week as complete for week:', format(weekToConfirm, 'yyyy-MM-dd'));
      
      // Calcular total de horas registradas desde projectData (exclude TOTAL_DIA row)
      const totalHours = projectData.filter(p => !p.isTotal).reduce((sum, project) => {
        return sum + (project.totalWeeklyHours || 0);
      }, 0);
      
      const expectedHours = getExpectedWeeklyHours(weekToConfirm);
      
      // Confirmar semana a través de la API
      const confirmData = {
        persona_id: parseInt(selectedPerson),
        fecha_inicio_semana: format(weekToConfirm, 'yyyy-MM-dd'),
        total_horas_registradas: totalHours,
        horas_esperadas: expectedHours
      };
      
      const response = await semanasConfirmadasAPI.confirmar(confirmData);
      console.log('✅ Semana confirmada en API:', response.data);
      
      // Actualizar estado local inmediatamente
      const weekKey = getWeekKey(weekToConfirm, selectedPerson);
      setConfirmedWeeks(prev => new Set([...prev, weekKey]));
      
      // Update states to reflect locked week
      setCanConfirmWeek(false);
      setHasUnsavedChanges(false);
      setShowConfirmWeekDialog(false); // Cerrar el modal de confirmación
      
      // Asegurar que permanecemos en la misma semana
      if (format(currentWeekStart, 'yyyy-MM-dd') !== format(weekToConfirm, 'yyyy-MM-dd')) {
        console.log('🔄 Restaurando semana confirmada:', format(weekToConfirm, 'yyyy-MM-dd'));
        setCurrentWeekStart(weekToConfirm);
      }
      
      // Reload data to update editability and show blocked state
      await loadPersonDailyData();
      
      console.log('🔒 Semana confirmada y calendario actualizado con estado bloqueado');
      
    } catch (error) {
      console.error('Error confirming week completion:', error);
      const errorMessage = error.response?.data?.error 
        || error.response?.data?.message 
        || error.message 
        || 'Error al confirmar la semana como completa';
      setErrors([errorMessage]);
      
      // Solo mostrar modal en caso de error
      showNotification(
        '❌ Error',
        errorMessage,
        'error'
      );
    }
  };

  const goToCurrentWeek = () => {
    const today = new Date();
    const weekStart = startOfWeek(today, { weekStartsOn: 1 });
    
    console.log('🏃 goToCurrentWeek called - Current week vs today week:', {
      currentWeek: format(currentWeekStart, 'yyyy-MM-dd'),
      todayWeek: format(weekStart, 'yyyy-MM-dd'),
      willChange: format(weekStart, 'yyyy-MM-dd') !== format(currentWeekStart, 'yyyy-MM-dd')
    });
    
    if (format(weekStart, 'yyyy-MM-dd') !== format(currentWeekStart, 'yyyy-MM-dd')) {
      console.log('🔄 Navigating to current week:', format(weekStart, 'yyyy-MM-dd'));
      setCurrentWeekStart(weekStart);
    } else {
      console.log('✅ Already on current week');
    }
  };

  // Save changes
  const handleSaveChanges = async () => {
    if (!selectedPerson) return;
    
    setLoading(true);
    setErrors([]);
    
    try {
      // Validate all days have maximum hours or less
      const dayErrors = [];
      const dayTotals = {};
      
      // Calculate totals for each day across all projects
      weekDays.forEach(day => {
        dayTotals[day.dateKey] = 0;
      });
      
      projectData.filter(p => !p.isTotal).forEach(project => {
        weekDays.forEach(day => {
          const dayData = project.days[day.dateKey];
          if (dayData) {
            dayTotals[day.dateKey] += dayData.hours || 0;
          }
        });
      });
      
      // Validate day totals
      weekDays.forEach(day => {
        if (dayTotals[day.dateKey] > day.maxHours) {
          dayErrors.push(`${day.label}: ${dayTotals[day.dateKey]}h (máximo ${day.maxHours}h)`);
        }
      });

      if (dayErrors.length > 0) {
        setErrors(['Errores de validación:', ...dayErrors]);
        setLoading(false);
        return;
      }

      console.log('Starting save process for person:', selectedPerson);
      
      // Get date range for current week
      const startDate = currentWeekStart;
      const endDate = addDays(currentWeekStart, 4);
      
      console.log('Date range:', {
        start: format(startDate, 'yyyy-MM-dd'),
        end: format(endDate, 'yyyy-MM-dd')
      });
      
      // Get existing dedications for the person in the current week to delete them
      const existingResponse = await dedicacionesAPI.getByPersona(selectedPerson, {
        fecha__gte: format(startDate, 'yyyy-MM-dd'),
        fecha__lte: format(endDate, 'yyyy-MM-dd')
      });
      
      const existingDedications = existingResponse.data?.results || existingResponse.data || [];
      
      // Filter to ensure we only get dedications within the exact week range
      const filteredExistingDedications = existingDedications.filter(dedication => {
        const dedicationDate = parseISO(dedication.fecha);
        return dedicationDate >= startDate && dedicationDate <= endDate;
      });
      
      console.log('Existing dedications to delete:', {
        totalFound: existingDedications.length,
        inCurrentWeek: filteredExistingDedications.length,
        weekRange: `${format(startDate, 'yyyy-MM-dd')} to ${format(endDate, 'yyyy-MM-dd')}`,
        dedicationsToDelete: filteredExistingDedications.map(d => ({ id: d.id, fecha: d.fecha, horas: d.horas || d.horas_imputadas }))
      });
      
      // Delete existing dedications (only for current week)
      if (filteredExistingDedications.length > 0) {
        console.log(`Deleting ${filteredExistingDedications.length} existing dedications for current week only`);
        await Promise.all(
          filteredExistingDedications.map(dedication => 
            dedicacionesAPI.delete(dedication.id).catch(err => {
              console.warn('Error deleting dedication:', err);
              return null;
            })
          )
        );
      } else {
        console.log('No existing dedications found for current week - nothing to delete');
      }

      // Prepare new dedications data (save as daily data)
      const dedicationsToSave = [];
      
      projectData.filter(p => !p.isTotal).forEach(project => {
        weekDays.forEach(day => {
          const dayData = project.days[day.dateKey];
          if (dayData && dayData.hours > 0) {
            const hours = dayData.hours;
            dedicationsToSave.push({
              persona: parseInt(selectedPerson),
              iniciativa: parseInt(project.projectId),
              fecha: format(day.date, 'yyyy-MM-dd'),
              horas: parseFloat(hours).toFixed(2),
              descripcion: dayData.isCheckbox 
                ? `${project.projectName} - Día completo (${hours}h)` 
                : `Imputación diaria - ${hours}h`
            });
          }
        });
      });

      console.log('New dedications to save:', dedicationsToSave);

      // Save new dedications using bulk create if available, otherwise individual creates
      if (dedicationsToSave.length > 0) {
        try {
          // Try bulk create first
          await dedicacionesAPI.bulkCreate(dedicationsToSave);
          console.log('Bulk create successful');
        } catch (bulkError) {
          console.warn('Bulk create failed, falling back to individual creates:', bulkError);
          // Fall back to individual creates
          const saveResults = await Promise.allSettled(
            dedicationsToSave.map(dedication => dedicacionesAPI.create(dedication))
          );
          
          const failedSaves = saveResults.filter(result => result.status === 'rejected');
          if (failedSaves.length > 0) {
            console.error('Some saves failed:', failedSaves);
            throw new Error(`${failedSaves.length} dedicaciones fallaron al guardarse`);
          }
        }
      }

      setErrors([]);
      setHasUnsavedChanges(false); // Mark changes as saved
      
      // Check if week can be confirmed now that it's saved
      const expectedHours = getExpectedWeeklyHours(currentWeekStart);
      const totalHours = projectData.filter(p => !p.isTotal).reduce((sum, project) => sum + (project.totalWeeklyHours || 0), 0);
      const allDaysComplete = areAllWorkingDaysComplete();
      const weekCanBeConfirmed = totalHours >= expectedHours && allDaysComplete;
      
      setCanConfirmWeek(weekCanBeConfirmed);
      
      const successMessage = 'Cambios guardados exitosamente';
      
      if (weekCanBeConfirmed) {
        // Show confirmation dialog automatically
        setShowConfirmWeekDialog(true);
      }
      
      // Reload data to show the saved changes
      await loadPersonDailyData();
      
    } catch (error) {
      console.error('Error saving changes:', error);
      const errorMessage = error.response?.data?.detail 
        || error.response?.data?.message 
        || error.message 
        || 'Error al guardar los cambios';
      setErrors([errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  // DataGrid columns - TRANSPOSED VIEW
  const columns = [
    // First column: Project Name
    {
      field: 'projectName',
      headerName: 'Proyecto',
      minWidth: 300,
      width: 450,
      flex: 1.5,
      resizable: true,
      editable: false,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      headerAlign: 'left',
      align: 'left',
      renderCell: (params) => {
        if (!params || !params.row) {
          return <Box></Box>;
        }
        
        // Special styling for TOTAL ROW
        if (params.row.isTotal) {
          return (
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center',
              justifyContent: 'flex-start',
              width: '100%',
              px: 2
            }}>
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 'bold',
                  color: 'primary.main',
                  fontSize: '1rem'
                }}
              >
                {params.row.projectName}
              </Typography>
            </Box>
          );
        }
        
        const project = params.row.project;
        const projectName = params.row.projectName;
        
        return (
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'flex-start',
            textAlign: 'left',
            gap: 1,
            width: '100%',
            px: 2
          }}>
            <Tooltip title={projectName} arrow>
              <Typography 
                variant="body2" 
                sx={{ 
                  fontWeight: 'bold',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  flex: 1
                }}
              >
                {projectName}
              </Typography>
            </Tooltip>
            {project && project.tipo_de_iniciativa === 'generica' && (
              <IconButton 
                size="small" 
                onClick={() => removeProject(project.id)}
                sx={{ padding: '2px', minWidth: 'auto' }}
                title="Eliminar iniciativa genérica"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        );
      }
    },
    // Next 5 columns: One for each day (Monday to Friday)
    ...weekDays.map(day => ({
      field: `day_${day.dateKey}`,
      headerName: day.shortLabel,
      minWidth: 100,
      width: 120,
      flex: 1,
      resizable: true,
      editable: false,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      type: 'number',
      headerAlign: 'center',
      align: 'center',
      renderHeader: (params) => {
        return (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            justifyContent: 'center',
            gap: 0.5,
            width: '100%'
          }}>
            <Typography 
              variant="caption" 
              sx={{ 
                fontWeight: 'bold',
                fontSize: '0.75rem',
                lineHeight: 1.2,
                textAlign: 'center',
                color: day.isToday ? 'primary.main' : 'inherit'
              }}
            >
              {day.shortLabel}
            </Typography>
            {day.isHoliday && (
              <Chip
                label="FESTIVO"
                size="small"
                color="error"
                variant="filled"
                sx={{ fontSize: '0.6rem', height: '16px' }}
              />
            )}
          </Box>
        );
      },
      renderCell: (params) => {
        if (!params || !params.row || !projectData || projectData.length === 0) {
          return <Box sx={{ width: '60px' }}></Box>;
        }
        
        const project = projectData.find(p => p && p.id === params.id);
        if (!project || !project.days) {
          return <Box sx={{ width: '60px' }}></Box>;
        }
        
        const dayData = project.days[day.dateKey];
        if (!dayData) {
          return <Box sx={{ width: '60px' }}></Box>;
        }
        
        // Special styling for TOTAL ROW - show day total with color coding
        if (project.isTotal) {
          const dayTotal = dayData.hours || 0;
          const dayMaxHours = day.maxHours || 0;
          const isComplete = dayTotal >= dayMaxHours && dayMaxHours > 0;
          const isOverLimit = dayTotal > dayMaxHours && dayMaxHours > 0;
          
          return (
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              width: '80px',
              height: '40px'
            }}>
              <Chip
                label={day.isHoliday ? 'FESTIVO' : `${dayTotal}h / ${dayMaxHours}h`}
                size="small"
                color={day.isHoliday ? 'error' : (isOverLimit ? 'error' : isComplete ? 'success' : 'default')}
                variant={day.isHoliday || isComplete ? 'filled' : 'outlined'}
                sx={{ 
                  fontSize: '0.75rem', 
                  height: '24px',
                  fontWeight: 'bold',
                  minWidth: '70px'
                }}
              />
            </Box>
          );
        }
        
        // Show checkbox for vacation/leave projects
        if (dayData.isCheckbox) {
          const dayMaxHours = day.maxHours || (isBecario ? 5 : 8);
          const weekConfirmed = isCurrentWeekConfirmed();
          const checkboxEditable = dayData.isEditable && !weekConfirmed;
          const getTooltipText = () => {
            if (day.isHoliday) return "Día festivo - No se pueden imputar horas";
            if (weekConfirmed) return "Semana confirmada - No se pueden modificar las horas";
            if (!dayData.isEditable) {
              const projectState = project.project.estado;
              if (projectState && projectState !== 'en_curso') {
                return `Proyecto bloqueado - Estado: ${projectState} (solo proyectos 'En curso' son editables)`;
              } else {
                return "Solo lectura (semana bloqueada o proyecto inactivo)";
              }
            }
            return dayData.isChecked ? `Día completo (${dayMaxHours}h)` : `Marcar día completo (${dayMaxHours}h)`;
          };

          return (
            <Tooltip title={getTooltipText()}>
              <Checkbox
                checked={dayData.isChecked || false}
                onChange={(e) => {
                  if (!project || !project.projectId || !checkboxEditable) return;
                  updateProjectCheckbox(project.projectId, day.dateKey, e.target.checked);
                }}
                color="primary"
                disabled={!checkboxEditable || day.isHoliday}
                sx={{ 
                  padding: '4px',
                  opacity: (checkboxEditable && !day.isHoliday) ? 1 : 0.6
                }}
              />
            </Tooltip>
          );
        }
        
        // Show regular hour input for normal projects
        const hours = dayData.hours || 0;
        const weekConfirmed = isCurrentWeekConfirmed();
        const isInputEditable = dayData.isEditable && !day.isHoliday && !weekConfirmed;
        
        const getTooltipText = () => {
          if (day.isHoliday) return "Día festivo - No se pueden imputar horas";
          if (weekConfirmed) return "Semana confirmada - No se pueden modificar las horas";
          if (!dayData.isEditable) {
            // Determine why it's not editable
            const isGenericProject = project.project.tipo_de_iniciativa === 'generica';
            const isHistoricalProject = project.project.estado === 'historical';
            const projectState = project.project.estado;
            
            if (isGenericProject) {
              return "Solo lectura (datos históricos)";
            } else if (isHistoricalProject) {
              return "Proyecto histórico (solo consulta)";
            } else if (projectState && projectState !== 'en_curso') {
              return `Estado: ${projectState} (solo proyectos 'En curso' son editables)`;
            } else {
              return "Solo lectura (semana bloqueada o proyecto inactivo)";
            }
          }
          return "Introduce las horas trabajadas";
        };
        
        return (
          <Tooltip title={getTooltipText()}>
            <TextField
              type="number"
              value={hours}
              onChange={(e) => {
                if (!project || !project.projectId || !isInputEditable) return;
                const newHours = Math.max(0, parseFloat(e.target.value) || 0);
                updateProjectHours(project.projectId, day.dateKey, newHours);
              }}
              disabled={!isInputEditable}
              inputProps={{ 
                min: 0, 
                max: day.maxHours || 8, 
                step: 0.5,
                readOnly: !isInputEditable
              }}
              size="small"
              sx={{ 
                width: '80px',
                '& input': { 
                  textAlign: 'center',
                  fontSize: '0.9rem',
                  backgroundColor: isInputEditable ? 'inherit' : (day.isHoliday ? '#ffebee' : '#f5f5f5'),
                  cursor: isInputEditable ? 'text' : 'not-allowed',
                  color: day.isHoliday ? '#d32f2f' : 'inherit'
                },
                '& .MuiInputBase-root': {
                  opacity: isInputEditable ? 1 : 0.6
                }
              }}
            />
          </Tooltip>
        );
      },
      valueGetter: (params) => {
        if (!params || !params.id || !params.row || !projectData || projectData.length === 0) {
          return 0;
        }
        const project = projectData.find(p => p && p.id === params.id);
        return project && project.days && project.days[day.dateKey] ? (project.days[day.dateKey].hours || 0) : 0;
      }
    })),
    // Last column: Weekly Total
    {
      field: 'totalWeeklyHours',
      headerName: 'TOTAL',
      minWidth: 130,
      width: 150,
      flex: 1,
      resizable: true,
      editable: false,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      type: 'number',
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => {
        if (!params || !params.row || !projectData || projectData.length === 0) {
          return <Box sx={{ width: '60px' }}></Box>;
        }
        
        const project = projectData.find(p => p && p.id === params.id);
        if (!project) {
          return <Box sx={{ width: '60px' }}></Box>;
        }
        
        const totalHours = project.totalWeeklyHours || 0;
        
        // Special styling for TOTAL ROW - show total week hours
        if (project.isTotal) {
          return (
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              width: '70px',
              height: '40px'
            }}>
              <Chip
                label={`${totalHours}h`}
                size="small"
                color="primary"
                variant="filled"
                sx={{ 
                  fontSize: '0.85rem', 
                  height: '26px',
                  fontWeight: 'bold',
                  minWidth: '60px'
                }}
              />
            </Box>
          );
        }
        
        const isVacationProject = isVacationOrLeave(project.project);
        
        // Determine if weekly total is editable
        let isWeeklyEditable = false;
        
        // First check: if week is confirmed, NOTHING is editable
        if (!isCurrentWeekConfirmed()) {
          // Only if week is NOT confirmed, check other conditions
          if (!isVacationProject) {
            // Check project conditions (same as daily cell logic)
            const isGenericProject = project.project.tipo_de_iniciativa === 'generica';
            const isHistoricalProject = project.project.estado === 'historical';
            if (isGenericProject || project.project.estado === 'en_curso' || isHistoricalProject) {
              isWeeklyEditable = true;
            }
          }
        }
        
        const getWeeklyTooltipText = () => {
          if (isCurrentWeekConfirmed()) {
            return "Semana confirmada - No se pueden modificar las horas";
          }
          if (isVacationProject) {
            return "Proyectos de ausencia/vacaciones se gestionan día a día";
          }
          if (!isWeeklyEditable && !isVacationProject) {
            return `Proyecto bloqueado (${project.project.estado}) - No editable`;
          }
          return "Editar total semanal - se distribuirá proporcionalmente entre los días laborales";
        };
        
        return (
          <Tooltip title={getWeeklyTooltipText()}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              width: '60px',
              height: '40px'
            }}>
              {isWeeklyEditable ? (
                <TextField
                  type="number"
                  value={totalHours}
                  onChange={(e) => {
                    const newWeeklyHours = Math.max(0, parseFloat(e.target.value) || 0);
                    updateProjectWeeklyHours(project.projectId, newWeeklyHours);
                  }}
                  inputProps={{ 
                    min: 0, 
                    max: getExpectedWeeklyHours(currentWeekStart),
                    step: 0.5
                  }}
                  size="small"
                  sx={{ 
                    width: '70px',
                    '& input': { 
                      textAlign: 'center',
                      fontSize: '0.9rem',
                      fontWeight: 'bold',
                      color: 'primary.main',
                      padding: '4px'
                    }
                  }}
                />
              ) : (
                <Box sx={{ 
                  backgroundColor: totalHours > 0 ? 'primary.light' : 'transparent',
                  borderRadius: 1,
                  border: totalHours > 0 ? '2px solid' : 'none',
                  borderColor: 'primary.main',
                  padding: '4px 8px',
                  minWidth: '50px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontWeight: 'bold',
                      color: totalHours > 0 ? 'primary.main' : 'text.secondary',
                      fontSize: '0.9rem'
                    }}
                  >
                    {totalHours > 0 ? `${totalHours}h` : '-'}
                  </Typography>
                </Box>
              )}
            </Box>
          </Tooltip>
        );
      }
    }
  ];

  console.log('🔍 Debug info:', {
    selectedPerson,
    selectedPersonName,
    projectsCount: projects.length,
    projectsList: projects.map(p => ({ id: p.id, nombre: p.nombre_de_la_oportunidad })),
    projectDataCount: projectData.length,
    columnsCount: columns.length
  }); // Debug log

  return (
    <Box sx={{ p: 3 }}>

      {/* Admin Person Selector */}
      {isAdmin && (
        <Box sx={{ mb: 3, maxWidth: 400 }}>
          <FormControl fullWidth>
            <InputLabel sx={{ color: 'black' }}>
              Seleccionar Persona ({people.length} disponibles)
            </InputLabel>
            <Select
              value={selectedPerson}
              onChange={(e) => {
                const personId = e.target.value;
                setSelectedPerson(personId);
                const person = people.find(p => p.id === personId);
                const personName = person?.full_name || person?.nombre || `Usuario ${personId}`;
                setSelectedPersonName(personName);
                setIsBecario(checkIsBecario(person?.email || ''));
              }}
              label="Seleccionar Persona"
              sx={{ 
                color: 'black',
                '& .MuiSelect-select': {
                  color: 'black !important'
                },
                '& .MuiInputLabel-root': {
                  color: 'black'
                }
              }}
            >
              {people.length === 0 ? (
                <MenuItem disabled sx={{ color: 'black' }}>
                  No hay personas disponibles
                </MenuItem>
              ) : (
                people.map(person => (
                  <MenuItem 
                    key={person.id} 
                    value={person.id}
                    sx={{ 
                      color: 'black !important',
                      backgroundColor: 'white',
                      '&:hover': {
                        backgroundColor: '#f5f5f5'
                      },
                      '& span': {
                        color: 'black !important'
                      },
                      whiteSpace: 'nowrap',
                      overflow: 'visible'
                    }}
                  >
                    <span style={{ color: 'black', display: 'block', width: '100%' }}>
                      {person.full_name || `${person.nombre} ${person.apellido}`.trim() || person.nombre || `Usuario ${person.id}`}
                    </span>
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
        </Box>
      )}

      {/* Navigation and Controls */}
      {selectedPerson && (
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6">
              Calendario para: <strong>{selectedPersonName}</strong>
            </Typography>
            
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              {/* Navegación anterior */}
              <IconButton 
                onClick={() => {
                  const newWeek = addWeeks(currentWeekStart, -1);
                  console.log('Navigating to previous week:', format(newWeek, 'yyyy-MM-dd'));
                  setCurrentWeekStart(newWeek);
                }}
                sx={{ 
                  bgcolor: 'grey.100',
                  '&:hover': { bgcolor: 'grey.200' }
                }}
              >
                <ChevronLeftIcon />
              </IconButton>
              
              {/* Selector de semana actual */}
              <Button 
                variant={isCurrentWeek ? "contained" : "outlined"}
                color="primary"
                startIcon={<TodayIcon />}
                onClick={goToCurrentWeek}
                sx={{ 
                  minWidth: '140px',
                  fontWeight: 'bold'
                }}
              >
                {isCurrentWeek ? 'Semana Actual' : 'Ir a Hoy'}
              </Button>
              
              {/* Selector de calendario directo */}
              <Box sx={{ position: 'relative', display: 'inline-block' }}>
                <input
                  type="date"
                  ref={hiddenDateInputRef}
                  style={{ 
                    position: 'absolute',
                    top: '100%',
                    left: '0',
                    width: '1px',
                    height: '1px',
                    opacity: 0,
                    zIndex: 1000,
                    pointerEvents: 'none'
                  }}
                  onChange={(e) => {
                    console.log('📅 Date input changed:', e.target.value);
                    if (e.target.value) {
                      const selectedDate = new Date(e.target.value);
                      const weekStart = startOfWeek(selectedDate, { weekStartsOn: 1 });
                      console.log('Navigating to week of selected date:', format(selectedDate, 'yyyy-MM-dd'), '-> week:', format(weekStart, 'yyyy-MM-dd'));
                      setCurrentWeekStart(weekStart);
                    } else {
                      console.log('❌ No date value received');
                    }
                  }}
                />
                <IconButton 
                  onClick={() => {
                    console.log('🔍 Calendar icon clicked');
                    console.log('📝 hiddenDateInputRef.current:', hiddenDateInputRef.current);
                    if (hiddenDateInputRef.current) {
                      console.log('✅ Input exists, trying different methods...');
                      
                      try {
                        // Método 1: showPicker (más moderno)
                        if (hiddenDateInputRef.current.showPicker) {
                          console.log('🎯 Using showPicker()');
                          hiddenDateInputRef.current.showPicker();
                        } else {
                          // Método 2: focus (más compatible)
                          console.log('🎯 Using focus()');
                          hiddenDateInputRef.current.focus();
                        }
                      } catch (error) {
                        console.log('❌ Error opening date picker:', error);
                        // Método 3: click como fallback
                        console.log('🎯 Fallback to click()');
                        hiddenDateInputRef.current.click();
                      }
                    } else {
                      console.log('❌ hiddenDateInputRef.current is null');
                    }
                  }}
                  sx={{ 
                    bgcolor: 'grey.100',
                    '&:hover': { 
                      bgcolor: 'primary.50',
                      color: 'primary.main'
                    }
                  }}
                  title="Seleccionar fecha"
                >
                  <CalendarIcon />
                </IconButton>
              </Box>
              
              {/* Navegación siguiente */}
              <IconButton 
                onClick={() => {
                  const newWeek = addWeeks(currentWeekStart, 1);
                  console.log('Navigating to next week:', format(newWeek, 'yyyy-MM-dd'));
                  setCurrentWeekStart(newWeek);
                }}
                sx={{ 
                  bgcolor: 'grey.100',
                  '&:hover': { bgcolor: 'grey.200' }
                }}
              >
                <ChevronRightIcon />
              </IconButton>
              
              {/* Indicador de semana */}
              <Chip
                label={`${format(currentWeekStart, 'dd/MM')} - ${format(addDays(currentWeekStart, 4), 'dd/MM')}`}
                color={isCurrentWeek ? 'primary' : 'default'}
                variant={isCurrentWeek ? 'filled' : 'outlined'}
                sx={{ 
                  fontWeight: 'bold',
                  fontSize: '0.875rem'
                }}
              />
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <Button
              variant="contained"
              color="success"
              startIcon={<SaveIcon />}
              onClick={handleSaveChanges}
              disabled={loading || projectData.length === 0 || isCurrentWeekConfirmed()}
            >
              {isCurrentWeekConfirmed() ? 'Semana Completada' : 'Guardar Cambios'}
            </Button>

            {/* Week completion status and confirm button - only show after saving and if not confirmed */}
            {canConfirmWeek && !hasUnsavedChanges && !isCurrentWeekConfirmed() && (
              <Button
                variant="outlined"
                color="primary"
                onClick={handleConfirmWeekComplete}
                disabled={loading}
                sx={{
                  borderColor: '#4caf50',
                  color: '#4caf50',
                  '&:hover': {
                    borderColor: '#388e3c',
                    backgroundColor: 'rgba(76, 175, 80, 0.04)'
                  }
                }}
              >
                ✅ Confirmar Semana Completa
              </Button>
            )}
            
            {(!canConfirmWeek || hasUnsavedChanges || isCurrentWeekConfirmed()) && (
              <Chip
                label={
                  isCurrentWeekConfirmed()
                    ? `${weeklyTotalHours}h / ${getExpectedWeeklyHours(currentWeekStart)}h`
                    : hasUnsavedChanges 
                      ? `${weeklyTotalHours}h / ${getExpectedWeeklyHours(currentWeekStart)}h (Sin guardar)`
                      : areAllWorkingDaysComplete() 
                        ? `${weeklyTotalHours}h / ${getExpectedWeeklyHours(currentWeekStart)}h (Días completos)`
                        : `${weeklyTotalHours}h / ${getExpectedWeeklyHours(currentWeekStart)}h (Faltan días)`
                }
                color={
                  isCurrentWeekConfirmed()
                    ? 'success'
                    : hasUnsavedChanges 
                      ? 'warning'
                      : (weeklyTotalHours >= getExpectedWeeklyHours(currentWeekStart) && areAllWorkingDaysComplete()) 
                        ? 'success' 
                        : 'default'
                }
                variant={isCurrentWeekConfirmed() ? 'filled' : 'outlined'}
                size="medium"
              />
            )}
            
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => setGenericProjectsDialog(true)}
              disabled={loading || projectData.length === 0 || isCurrentWeekConfirmed()}
              sx={{
                opacity: isCurrentWeek ? 1 : 0.8,
                '&:hover': {
                  opacity: 1
                }
              }}
            >
              Agregar Iniciativa Genérica
            </Button>
          </Box>
        </Box>
      )}

      {/* Error Messages */}
      {errors.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errors.map((error, index) => (
            <div key={index}>{error}</div>
          ))}
        </Alert>
      )}

      {/* Calendar DataGrid */}
      {selectedPerson && (
        <Box sx={{ 
          width: '100%',
          '& .MuiDataGrid-root': {
            border: 'none'
          }
        }}>
          <DataGrid
            rows={projectData}
            columns={columns}
            loading={loading}
            disableRowSelectionOnClick
            disableColumnMenu
            disableColumnFilter
            disableColumnSort
            hideFooter
            autoHeight
            processRowUpdate={processRowUpdate}
            onProcessRowUpdateError={handleProcessRowUpdateError}
            sx={{
              border: 'none',
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: 'white',
                color: 'black',
                fontWeight: 'bold',
                borderBottom: '2px solid #e0e0e0',
                minHeight: '48px'
              },
              '& .MuiDataGrid-columnHeaderTitle': {
                fontWeight: 'bold',
                textAlign: 'center',
              },
              '& .MuiDataGrid-columnHeader:first-of-type .MuiDataGrid-columnHeaderTitle': {
                textAlign: 'left !important',
              },
              '& .MuiDataGrid-columnHeader': {
                justifyContent: 'center',
                padding: '4px 2px',
                overflow: 'visible',
              },
              '& .MuiDataGrid-columnHeader:first-of-type': {
                justifyContent: 'flex-start !important',
                paddingLeft: '16px',
              },
              '& .MuiDataGrid-cell': {
                textAlign: 'center',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '4px 2px',
                overflow: 'visible',
              },
              '& .MuiDataGrid-cell:first-of-type': {
                justifyContent: 'flex-start !important',
                textAlign: 'left',
                paddingLeft: '0px',
              },
              '& .MuiDataGrid-row': {
                '&:hover': {
                  backgroundColor: '#f5f5f5',
                },
                minHeight: '52px !important',
                // Special styling for TOTAL DIA row
                '&[data-id="TOTAL_DIA"]': {
                  backgroundColor: '#e3f2fd',
                  borderTop: '2px solid #1976d2',
                  borderBottom: '2px solid #1976d2',
                  minHeight: '56px !important',
                  '&:hover': {
                    backgroundColor: '#bbdefb',
                  },
                  '& .MuiDataGrid-cell': {
                    fontWeight: 'bold',
                    padding: '8px 4px'
                  }
                }
              }
            }}
          />
        </Box>
      )}

      {/* Instructions */}
      {selectedPerson && (
        <Alert severity="info" sx={{ mt: 2 }}>
          <Typography variant="body2">
            <strong>Instrucciones:</strong><br/>
            • Edita horas por día individualmente o usa la columna <strong>TOTAL</strong> para distribuir automáticamente<br/>
            • {isBecario ? 'Máximo 25h: 5h/día (Lun-Vie). Los festivos están bloqueados' : 'Máximo 38h: 8h/día (Lun-Jue), 6h (Viernes). Los festivos están bloqueados'}<br/>
            • <strong>Importante:</strong> Guarda los cambios y confirma la semana cuando esté completa. Una vez confirmada, no se puede modificar
          </Typography>
        </Alert>
      )}

      {selectedPerson && projectData.length === 0 && !loading && (
        <Alert severity="info" sx={{ mt: 2 }}>
          <Typography variant="body2">
            Esta persona no tiene proyectos asociados actualmente. Los proyectos deben ser asignados por el jefe de proyecto durante la creación del proyecto.
          </Typography>
        </Alert>
      )}

      {/* Dialog for confirming week completion */}
      <Dialog 
        open={showConfirmWeekDialog} 
        onClose={() => setShowConfirmWeekDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Confirmar Semana Completa
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            ¿Estás seguro de que has completado esta semana?
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            <strong>Semana:</strong> {format(currentWeekStart, 'dd/MM/yyyy', { locale: es })} al {format(addDays(currentWeekStart, 4), 'dd/MM/yyyy', { locale: es })}
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            <strong>Total de horas:</strong> {weeklyTotalHours}h de {getExpectedWeeklyHours(currentWeekStart)}h esperadas
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            <strong>Días laborales:</strong> {areAllWorkingDaysComplete() ? 'Todos completados ✓' : 'Algunos incompletos ⚠️'}
          </Typography>
          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>⚠️ Atención:</strong> Una vez confirmada, esta semana quedará bloqueada y no podrás modificar las horas imputadas.
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowConfirmWeekDialog(false)}>
            Cancelar
          </Button>
          <Button onClick={confirmWeekCompletion} variant="contained" color="primary">
            Confirmar y Bloquear Semana
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog for adding generic projects */}
      <Dialog 
        open={genericProjectsDialog} 
        onClose={() => {
          setSelectedGenericProjects(new Set());
          setGenericProjectsDialog(false);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Agregar Iniciativas Genéricas
          {selectedGenericProjects.size > 0 && (
            <Chip 
              label={`${selectedGenericProjects.size} seleccionada${selectedGenericProjects.size > 1 ? 's' : ''}`}
              color="primary" 
              size="small" 
              sx={{ ml: 2 }}
            />
          )}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Selecciona una o más iniciativas genéricas para agregar a esta semana. Solo aparecen las iniciativas genéricas que están en estado "En curso".
          </Typography>
          
          {loadingGenericProjects ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
              <CircularProgress sx={{ mb: 2 }} />
              <Typography variant="body2" color="textSecondary">
                Cargando iniciativas genéricas...
              </Typography>
            </Box>
          ) : availableGenericProjects.length === 0 ? (
            <Typography variant="body2" color="textSecondary">
              No hay iniciativas genéricas disponibles para agregar.
            </Typography>
          ) : (
            <List>
              {availableGenericProjects.map(project => {
                const isSelected = selectedGenericProjects.has(project.id);
                return (
                  <ListItem 
                    key={project.id} 
                    button 
                    onClick={() => toggleProjectSelection(project.id)}
                    sx={{ 
                      border: '1px solid #e0e0e0',
                      borderRadius: 1,
                      mb: 1,
                      backgroundColor: isSelected ? '#e3f2fd' : 'transparent',
                      borderColor: isSelected ? '#1976d2' : '#e0e0e0',
                      borderWidth: isSelected ? '2px' : '1px',
                      cursor: 'pointer',
                      '&:hover': { 
                        backgroundColor: isSelected ? '#bbdefb' : '#f5f5f5',
                        cursor: 'pointer'
                      },
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <ListItemIcon>
                      <Checkbox
                        checked={isSelected}
                        color="primary"
                        sx={{ padding: '4px' }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={project.nombre_de_la_oportunidad}
                      secondary={`Tipo: ${project.tipo_de_iniciativa} | Estado: ${project.estado}`}
                      sx={{
                        '& .MuiListItemText-primary': {
                          fontWeight: isSelected ? 'bold' : 'normal'
                        }
                      }}
                    />
                  </ListItem>
                );
              })}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setSelectedGenericProjects(new Set());
            setGenericProjectsDialog(false);
          }}>
            Cancelar
          </Button>
          {selectedGenericProjects.size > 0 && (
            <>
              {/* <Tooltip title="Limpiar selección">
                <IconButton 
                  onClick={() => setSelectedGenericProjects(new Set())}
                  color="inherit"
                  sx={{ mr: 1 }}
                >
                  <ClearIcon />
                </IconButton>
              </Tooltip> */}
              <Button 
                onClick={addMultipleGenericProjects}
                variant="contained" 
                color="primary"
                startIcon={<AddIcon />}
              >
                Agregar {selectedGenericProjects.size} Iniciativa{selectedGenericProjects.size > 1 ? 's' : ''}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
      
      {/* Notification Modal */}
      <Dialog
        open={notificationModal.open}
        onClose={closeNotification}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            backgroundColor: notificationModal.type === 'success' ? 'success.light' : 
                           notificationModal.type === 'error' ? 'error.light' : 'info.light',
            color: 'white',
            textAlign: 'center'
          }}
        >
          {notificationModal.title}
        </DialogTitle>
        <DialogContent sx={{ pt: 3, textAlign: 'center' }}>
          <Typography
            variant="body1"
            sx={{ whiteSpace: 'pre-line', lineHeight: 1.6 }}
          >
            {notificationModal.message}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 2 }}>
          <Button
            onClick={closeNotification}
            variant="contained"
            color={notificationModal.type === 'success' ? 'success' : 
                   notificationModal.type === 'error' ? 'error' : 'primary'}
            sx={{ minWidth: 100 }}
          >
            Aceptar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TimeTrackingCalendar;